(function () {
  'use strict';

  const STORAGE_KEY = 'ana_isabel_style_blueprint_v1_1';
  const ALGORITHM_VERSION = '1.1-review';

  const shapeLabels = {
    apple: 'Apple',
    hourglass: 'Hourglass',
    inverted_triangle: 'Inverted Triangle',
    pear: 'Pear',
    rectangle: 'Rectangle'
  };

  const preferenceLabels = {
    defined: 'Defined and tailored',
    soft: 'Softly shaped',
    relaxed: 'Relaxed and fluid',
    mixed: 'A mix'
  };

  const guidanceLabels = {
    waist_definition: 'Create more waist definition in my outfits',
    balanced_silhouette: 'Choose pieces that create a more balanced silhouette',
    upper_lower_balance: 'Balance how my upper and lower proportions read',
    natural_proportions: 'Find pieces that work better with my natural proportions',
    elegant_put_together: 'Choose outfits that feel elegant and put-together'
  };

  const guidanceLensCopy = {
    waist_definition: 'When you browse, look first at waist placement, seams, belts, shaping details, and whether the garment creates the level of definition you actually want.',
    balanced_silhouette: 'When you browse, look first at how the garment distributes visual weight and whether the silhouette feels balanced in the way you want it to read.',
    upper_lower_balance: 'When you browse, compare how the top and bottom of the look work together, including shoulder line, hip ease, hem placement, and overall proportion.',
    natural_proportions: 'When you browse, start with pieces that respect your natural proportions and construction needs before deciding how much shaping or emphasis you want.',
    elegant_put_together: 'When you browse, focus on clean structure, proportion, and finish so the final outfit feels polished, feminine, and easy to wear.'
  };

  const intentLabels = {
    dresses: 'dresses',
    tops: 'tops & blouses',
    bottoms: 'bottoms',
    sets: 'sets',
    jackets: 'blazers & jackets',
    general: 'general wardrobe guidance'
  };

  const fitLabels = {
    waist_hip_fit: 'Waist-to-hip fit',
    bust_shoulder_fit: 'Bust or shoulder fit',
    one_piece_balance: 'One-piece balance',
    length_shorter: 'Shorter length preference',
    length_longer: 'Longer length preference',
    none: 'No consistent fit pattern'
  };

  const fitGuidance = {
    waist_hip_fit: 'Compare waist and hip measurements separately. Look for shaped or adjustable waistbands, ties, darts, back elastic, stretch behavior, and enough ease through the hip.',
    bust_shoulder_fit: 'Check bust circumference, shoulder seam placement, sleeve mobility, closures, stretch, and whether gaining room through the upper body creates excess room elsewhere.',
    one_piece_balance: 'For dresses, jumpsuits, and sets, look for wraps, ties, stretch, adjustable details, or construction that can accommodate more than one proportion point.',
    length_shorter: 'Check the actual garment length and where it is likely to fall on you. Do not assume petite sizing exists unless the product specifically offers it.',
    length_longer: 'Check the actual garment length against the coverage you prefer. Do not assume tall sizing exists unless the product specifically offers it.',
    none: 'Use body structure, garment construction, and your silhouette preference as the main checkpoints, then compare the product measurements before ordering.'
  };

  const shapeGuidance = {
    apple: {
      intro: 'Your proportions most closely align with an Apple structure. The useful shopping question is not how to change your body, but how a garment handles midsection ease, bust room, waist placement, and vertical line.',
      general: 'Explore gentle shaping, clean vertical lines, open layers, and silhouettes that do not depend on a tightly cinched natural waist.',
      dresses: 'Explore wrap-inspired, A-line, softly structured shift, empire-influenced, or clean column directions. Pay attention to waist-seam placement and midsection ease.',
      tops: 'Look for clean vertical lines, open or softly shaped necklines, fluid or lightly structured drape, and enough ease through the midsection.',
      bottoms: 'Straight, bootcut, tapered, and wide-leg options can all work. Prioritize a smooth waistband and a leg line that supports the overall outfit.',
      sets: 'Look at the relationship between top length, waistband placement, and the visual line created where the two pieces meet.',
      jackets: 'Open-front, single-breasted, or vertically structured jackets are useful starting points. Check closure behavior and where the jacket ends.'
    },
    hourglass: {
      intro: 'Your proportions most closely align with an Hourglass structure. Bust and hip relationships are relatively balanced, while the waist creates clearer definition. How much of that definition you want to echo in clothing remains your choice.',
      general: 'Use waist placement as an optional styling tool rather than a rule. Wraps, seams, belts, soft shaping, or relaxed silhouettes can all work depending on preference.',
      dresses: 'Wrap, fit-and-flare, belted, tailored sheath, and other waist-aware silhouettes are useful starting points. Check whether one-piece construction accommodates bust, waist, and hips together.',
      tops: 'Fitted, softly shaped, wrap-style, or fluid tops that return toward the waist can be useful when you want definition.',
      bottoms: 'Mid- or high-rise straight, bootcut, flare, or wide-leg styles can work. Check waist-to-hip grading rather than choosing by one measurement alone.',
      sets: 'Use the meeting point between top and bottom to decide how much waist definition you want. Check both pieces rather than assuming one size solves every proportion.',
      jackets: 'Tailored, belted, cropped-to-waist, or gently shaped jackets can support definition. Longer relaxed jackets remain valid for a softer look.'
    },
    inverted_triangle: {
      intro: 'Your proportions most closely align with an Inverted Triangle structure. When visual balance is your goal, the lower silhouette can carry more movement or presence while shoulder fit stays comfortable and intentional.',
      general: 'Use clean shoulder construction and decide whether you want the lower silhouette to add movement or visual presence. Balance is an option, not an obligation.',
      dresses: 'A-line, fit-and-flare, wrap-inspired, or dresses with movement through the skirt are useful balance-oriented starting points.',
      tops: 'V, scoop, asymmetric, and open-collar lines can create a clean upper line. Statement necklines still work when upper-body emphasis is what you enjoy.',
      bottoms: 'Wide-leg, bootcut, pleated, textured, patterned, or fuller lower silhouettes can add presence below when that supports the look you want.',
      sets: 'Keep shoulder construction intentional and use the bottom half of the set to choose how much movement or lower-body presence you want.',
      jackets: 'Simple shoulder construction, open fronts, narrower lapel lines, or jackets without heavy shoulder embellishment are useful when your goal is balance.'
    },
    pear: {
      intro: 'Your proportions most closely align with a Pear structure. Lower-body fit and the relationship between waist and hips are likely to be useful checkpoints, while neckline, shoulder detail, and waist placement remain optional tools for visual balance.',
      general: 'Prioritize lower-body ease and waist-to-hip fit. Use neckline, shoulder detail, or waist definition only when those choices support the look you want.',
      dresses: 'A-line, fit-and-flare, wrap-inspired, belted, or other waist-aware silhouettes often provide practical lower-body ease and useful movement through the skirt.',
      tops: 'Boat, square, scoop, open collar, V, and neckline detail can all add upper-body interest when desired. Check where the top ends in relation to your outfit.',
      bottoms: 'Straight, bootcut, flare, wide-leg, or clean A-line shapes are useful starting points. Prioritize hip room and waist fit rather than choosing by waist alone.',
      sets: 'Check the waistband and lower-body ease separately from the top. Adjustable or shaped waists can be especially useful when the two pieces grade differently.',
      jackets: 'Waist-shaped, cropped, or longer jackets that end clearly above or below the fullest hip area are useful starting points to explore.'
    },
    rectangle: {
      intro: 'Your proportions most closely align with a Rectangle structure. You have two equally valid styling directions: create more visible shape through garment construction or preserve a clean, linear silhouette.',
      general: 'Choose whether you want definition or clean simplicity. Belts, wraps, seams, peplum, and cropped layers can create shape, while straight or column lines preserve linearity.',
      dresses: 'Belted, wrap, fit-and-flare, shirt dress, sheath, shift, and column styles can all work depending on whether you want definition or a cleaner straight line.',
      tops: 'Neckline choice is highly flexible. Square, scoop, V, crew, boat, and asymmetric shapes can all work depending on the effect you want.',
      bottoms: 'Pleated, wide-leg, tapered, straight, cargo-inspired, or fuller silhouettes can create different amounts of dimension and movement.',
      sets: 'Use the separation between top and bottom to create definition, layering, or a deliberately linear look. Pocket placement and proportions can change the effect significantly.',
      jackets: 'Tailored, belted, cropped, double-breasted, or straight relaxed jackets can create very different silhouettes. Match the jacket to your preferred level of definition.'
    }
  };

  const movementGuidance = {
    defined: 'Prioritize controlled structure, clearer seams, intentional shaping, and construction that holds the silhouette you selected.',
    soft: 'Look for gentle definition, controlled drape, wrap or tie elements, and movement without losing all shape.',
    relaxed: 'Prioritize ease, fluid fabrics, open layers, straighter lines, and comfortable movement without forcing waist definition.',
    mixed: 'Compare one more structured option with one softer or more fluid direction. Let the garment and occasion decide how much definition feels right.'
  };

  function toInches(value, unit) {
    return unit === 'cm' ? value / 2.54 : value;
  }

  function safeJsonParse(value) {
    try { return JSON.parse(value); } catch (error) { return null; }
  }

  function emitEvent(name, profile) {
    const safeProfile = profile || {};
    const payload = {
      event: name,
      blueprint_version: ALGORITHM_VERSION,
      body_shape: safeProfile.shape || undefined,
      shopping_interest: safeProfile.intent || undefined,
      silhouette_preference: safeProfile.preference || undefined,
      guidance_focus: safeProfile.guidance || undefined
    };
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    window.dispatchEvent(new CustomEvent('anaIsabelBlueprintEvent', { detail: payload }));
  }

  function initCalculator(root) {
    if (!root || root.dataset.aiBlueprintReady === 'true') return;
    root.dataset.aiBlueprintReady = 'true';

    const screens = {};
    root.querySelectorAll('[data-screen]').forEach((screen) => { screens[screen.dataset.screen] = screen; });
    const progress = root.querySelector('[data-progress]');
    const progressBar = root.querySelector('[data-progress-bar]');
    const progressLabel = root.querySelector('[data-progress-label]');
    const progressName = root.querySelector('[data-progress-name]');
    const savedPanel = root.querySelector('[data-saved-profile]');

    const state = {
      measurements: null,
      shape: null,
      secondary: null,
      proportion: null,
      fit: [],
      preference: null,
      guidance: null,
      intent: null,
      refinementShown: false
    };

    const stepMeta = {
      measurements: [1, 'Your proportions'],
      fit: [2, 'Your fit experience'],
      preference: [3, 'Your style goals'],
      result: [4, 'Your Style Blueprint']
    };

    function showScreen(name) {
      Object.keys(screens).forEach((key) => {
        const active = key === name;
        screens[key].hidden = !active;
        screens[key].classList.toggle('is-active', active);
      });
      if (name === 'intro') {
        progress.hidden = true;
      } else {
        progress.hidden = false;
        const meta = stepMeta[name] || [1, ''];
        progressLabel.textContent = `Step ${meta[0]} of 4`;
        progressName.textContent = meta[1];
        progressBar.style.width = `${meta[0] * 25}%`;
      }
      const calculator = root.querySelector('[data-calculator]');
      if (calculator && name !== 'intro') calculator.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function getUnit() {
      const selected = root.querySelector('[data-unit-fieldset] input:checked');
      return selected ? selected.value : 'inches';
    }

    function readMeasurements() {
      const unit = getUnit();
      const data = {};
      ['bust', 'waist', 'hips', 'highHip', 'midsection'].forEach((key) => {
        const field = root.querySelector(`[data-measure="${key}"]`);
        const raw = field && field.value !== '' ? parseFloat(field.value) : null;
        data[key] = raw === null || Number.isNaN(raw) ? null : toInches(raw, unit);
      });
      data.unit = unit;
      data.shoulders = (root.querySelector(`input[name^="ai-shoulders-"]:checked`) || {}).value || null;
      data.midCompare = (root.querySelector(`input[name^="ai-midsection-compare-"]:checked`) || {}).value || null;
      return data;
    }

    function validateMeasurements(data) {
      const error = root.querySelector('[data-measure-error]');
      error.hidden = true;
      error.textContent = '';
      if (!data.bust || !data.waist || !data.hips || data.bust <= 0 || data.waist <= 0 || data.hips <= 0) {
        error.textContent = 'Please enter positive numbers for bust, natural waist, and hips before continuing.';
        error.hidden = false;
        return false;
      }
      const required = [data.bust, data.waist, data.hips];
      const unusual = required.some((v) => v < 18 || v > 90);
      if (unusual) {
        error.textContent = 'One measurement looks unusual for the selected unit. Please check the unit and number before continuing.';
        error.hidden = false;
        return false;
      }
      if (data.highHip && (data.highHip < 15 || data.highHip > 90)) {
        error.textContent = 'Please check the optional high-hip measurement or leave it blank.';
        error.hidden = false;
        return false;
      }
      return true;
    }

    function determineRefinements(data) {
      const bustHipDiff = Math.abs(data.bust - data.hips);
      const waistNear = data.waist >= Math.min(data.bust, data.hips) - 3;
      return {
        shoulders: bustHipDiff <= 4.5,
        midsection: waistNear || data.waist >= Math.max(data.bust, data.hips) - 5
      };
    }

    function showRefinements(needs) {
      const wrapper = root.querySelector('[data-refinement]');
      const shoulders = root.querySelector('[data-shoulder-refinement]');
      const midsection = root.querySelector('[data-midsection-refinement]');
      shoulders.hidden = !needs.shoulders;
      midsection.hidden = !needs.midsection;
      wrapper.hidden = !(needs.shoulders || needs.midsection);
      if (!wrapper.hidden) wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function appleSignal(data) {
      const maxOuter = Math.max(data.bust, data.hips);
      const minOuter = Math.min(data.bust, data.hips);
      const measurementSignal = data.midsection && data.midsection >= maxOuter - 1 && data.waist >= minOuter - 4;
      const answerSignal = data.midCompare === 'yes' && data.waist >= minOuter - 4;
      const softerAnswerSignal = data.midCompare === 'somewhat' && data.waist >= minOuter - 2;
      const waistDominant = data.waist > maxOuter && (data.midCompare === 'yes' || data.midCompare === 'somewhat' || measurementSignal);
      return Boolean(measurementSignal || answerSignal || softerAnswerSignal || waistDominant);
    }

    function classify(data) {
      const bust = data.bust;
      const waist = data.waist;
      const hips = data.hips;
      const highHip = data.highHip;
      const bustHip = bust - hips;
      const hipBust = hips - bust;
      const bustWaist = bust - waist;
      const hipWaist = hips - waist;
      const highHipRatio = highHip ? highHip / waist : null;
      const waistDrop = Math.max(bustWaist, hipWaist);
      const closeOuter = Math.abs(bustHip) < 3.6;
      const apple = appleSignal(data);

      let shape;
      let internal = null;

      if (apple) {
        shape = 'apple';
        internal = 'midsection_dominant';
      } else if (bustHip <= 1 && hipBust < 3.6 && (bustWaist >= 9 || hipWaist >= 10)) {
        shape = 'hourglass';
        internal = 'hourglass';
      } else if (highHipRatio && hipBust >= 3.6 && hipBust < 10 && hipWaist >= 9 && highHipRatio < 1.193) {
        shape = 'hourglass';
        internal = 'bottom_hourglass';
      } else if (bustHip > 1 && bustHip < 10 && bustWaist >= 9) {
        shape = 'hourglass';
        internal = 'top_hourglass';
      } else if (highHipRatio && hipBust > 2 && hipWaist >= 7 && highHipRatio >= 1.193) {
        shape = 'pear';
        internal = 'spoon';
      } else if (hipBust >= 3.6 && hipWaist < 9) {
        shape = 'pear';
        internal = 'triangle';
      } else if (bustHip >= 3.6 && bustWaist < 9) {
        shape = 'inverted_triangle';
        internal = 'inverted_triangle';
      } else if (closeOuter && bustWaist < 9 && hipWaist < 10) {
        if (data.shoulders === 'wider') {
          shape = 'inverted_triangle';
          internal = 'shoulder_refined_inverted_triangle';
        } else if (data.shoulders === 'hips_wider') {
          shape = 'pear';
          internal = 'visual_refined_pear';
        } else {
          shape = 'rectangle';
          internal = 'rectangle';
        }
      } else if (hipBust >= 3.6 && hipWaist >= 9) {
        shape = 'pear';
        internal = 'lower_hourglass_leaning';
      } else if (bustHip >= 3.6 && bustWaist >= 9) {
        shape = 'hourglass';
        internal = 'upper_hourglass_leaning';
      } else if (hipBust >= 2.5) {
        shape = 'pear';
        internal = 'pear_heuristic';
      } else if (bustHip >= 2.5) {
        shape = 'inverted_triangle';
        internal = 'inverted_heuristic';
      } else if (waistDrop >= 7 && Math.abs(bustHip) < 4) {
        shape = 'hourglass';
        internal = 'hourglass_heuristic';
      } else {
        shape = 'rectangle';
        internal = 'rectangle_heuristic';
      }

      return { shape, internal, bustHip, hipBust, bustWaist, hipWaist, waistDrop };
    }

    function secondaryInfluence(shape, data, metrics) {
      const outerDiff = Math.abs(data.bust - data.hips);
      const definedWaist = metrics.waistDrop >= 8;
      if (shape === 'pear') {
        if (definedWaist && outerDiff < 6) return 'hourglass';
        if (outerDiff < 3.8 && !definedWaist) return 'rectangle';
      }
      if (shape === 'inverted_triangle') {
        if (definedWaist && outerDiff < 6) return 'hourglass';
        if (outerDiff < 3.8 && !definedWaist) return 'rectangle';
      }
      if (shape === 'hourglass') {
        if (data.hips - data.bust >= 2.5) return 'pear';
        if (data.bust - data.hips >= 2.5 || data.shoulders === 'wider') return 'inverted_triangle';
      }
      if (shape === 'rectangle') {
        if (data.shoulders === 'wider') return 'inverted_triangle';
        if (data.shoulders === 'hips_wider') return 'pear';
        if (metrics.waistDrop >= 7.5) return 'hourglass';
      }
      if (shape === 'apple' && outerDiff < 3.6 && metrics.waistDrop < 6) return 'rectangle';
      return null;
    }

    function proportionLabel(shape, data, metrics) {
      const waist = metrics.waistDrop >= 8 ? 'defined waist' : metrics.waistDrop >= 5 ? 'soft waist definition' : 'gentle waist definition';
      if (shape === 'pear') return `Lower-body emphasis with ${waist}`;
      if (shape === 'inverted_triangle') return `Upper-body emphasis with ${waist}`;
      if (shape === 'hourglass') return `Balanced bust and hips with ${waist}`;
      if (shape === 'apple') return 'Midsection-led proportion with softer waist definition';
      return `Balanced upper and lower proportions with ${waist}`;
    }

    function fitSummary(values) {
      if (!values.length) return fitLabels.none;
      return values.map((v) => fitLabels[v] || v).join(' + ');
    }

    function fitLens(values) {
      if (!values.length) return fitGuidance.none;
      const first = values[0];
      const second = values[1];
      if (!second) return fitGuidance[first] || fitGuidance.none;
      return `${fitGuidance[first]} ${fitGuidance[second]}`;
    }

    function shapeIntro(shape, secondary) {
      let text = shapeGuidance[shape].intro;
      if (secondary) text += ` Some of your proportions also show ${shapeLabels[secondary]} characteristics, so the Blueprint uses that as a secondary influence rather than forcing a single rigid label.`;
      return text;
    }

    function storeProfile(profile) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(profile)); } catch (error) { }
    }

    function getStoredProfile() {
      try { return safeJsonParse(localStorage.getItem(STORAGE_KEY)); } catch (error) { return null; }
    }

    function clearStoredProfile() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (error) { }
    }

    function updateEmailTags(profile) {
      const field = root.querySelector('[data-blueprint-tags]');
      if (!field || !profile) return;
      const fitTags = (profile.fit || []).filter((v) => v !== 'none').map((v) => `fit-${v.replace(/_/g, '-')}`);
      const tags = [
        'newsletter',
        'style-blueprint',
        `shape-${profile.shape.replace(/_/g, '-')}`,
        `preference-${profile.preference}`,
        `guidance-${profile.guidance}`,
        `shopping-${profile.intent}`,
        ...fitTags
      ];
      field.value = tags.join(',');
    }

    function renderProfile(profile) {
      state.shape = profile.shape;
      state.secondary = profile.secondary || null;
      state.proportion = profile.proportion;
      state.fit = profile.fit || [];
      state.preference = profile.preference;
      state.guidance = profile.guidance;
      state.intent = profile.intent;

      root.querySelector('[data-result-heading]').textContent = `${shapeLabels[profile.shape]} structure, with your preferences included`;
      root.querySelector('[data-result-intro]').textContent = shapeIntro(profile.shape, profile.secondary);
      root.querySelector('[data-result-primary]').textContent = shapeLabels[profile.shape];

      const secondaryCard = root.querySelector('[data-secondary-card]');
      if (profile.secondary) {
        root.querySelector('[data-result-secondary]').textContent = shapeLabels[profile.secondary];
        secondaryCard.hidden = false;
      } else {
        secondaryCard.hidden = true;
      }

      root.querySelector('[data-result-proportion]').textContent = profile.proportion;
      root.querySelector('[data-result-fit]').textContent = fitSummary(profile.fit || []);
      root.querySelector('[data-result-preference]').textContent = preferenceLabels[profile.preference];

      const guidanceField = root.querySelector('[data-result-guidance]');
      const guidanceCard = root.querySelector('[data-guidance-card]');
      if (guidanceField && guidanceCard && profile.guidance) {
        guidanceField.textContent = guidanceLabels[profile.guidance] || profile.guidance;
        guidanceCard.hidden = false;
      } else if (guidanceCard) {
        guidanceCard.hidden = true;
      }

      const guidanceSummary = root.querySelector('[data-guidance-summary]');
      if (guidanceSummary && profile.guidance) guidanceSummary.textContent = guidanceLensCopy[profile.guidance] || '';

      const guidance = shapeGuidance[profile.shape];
      root.querySelector('[data-lens-silhouette]').textContent = guidance[profile.intent] || guidance.general;
      root.querySelector('[data-lens-fit]').textContent = fitLens(profile.fit || []);
      root.querySelector('[data-lens-movement]').textContent = movementGuidance[profile.preference];

      const shapeUrl = root.dataset[`${profile.shape.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}Url`] || '/pages/shop';
      const intentUrl = root.dataset[`${profile.intent}Url`] || '/pages/shop';
      const shapeLink = root.querySelector('[data-shape-link]');
      const intentLink = root.querySelector('[data-intent-link]');
      shapeLink.href = shapeUrl;
      intentLink.href = intentUrl;
      shapeLink.textContent = `Explore ${shapeLabels[profile.shape]} collection`;
      intentLink.textContent = profile.intent === 'general' ? 'Explore the shop' : `Browse ${intentLabels[profile.intent]}`;
      root.querySelector('[data-shop-heading]').textContent = `Explore with your ${shapeLabels[profile.shape]} starting point in mind`;
      root.querySelector('[data-shop-copy]').textContent = `You said you are currently interested in ${intentLabels[profile.intent]}. Use your Shopping Lens while browsing, then review the measurements and construction of each individual piece.`;

      updateEmailTags(profile);
      showScreen('result');
    }

    function buildProfile() {
      const preference = (root.querySelector(`input[name^="ai-preference-"]:checked`) || {}).value;
      const guidanceChoice = (root.querySelector(`input[name^="ai-guidance-"]:checked`) || {}).value;
      const intent = (root.querySelector(`input[name^="ai-intent-"]:checked`) || {}).value;
      const error = root.querySelector('[data-preference-error]');
      error.hidden = true;
      if (!preference || !guidanceChoice || !intent) {
        error.textContent = 'Please choose a style preference, the kind of guidance you want most right now, and what you would like help shopping for.';
        error.hidden = false;
        return;
      }
      state.preference = preference;
      state.guidance = guidanceChoice;
      state.intent = intent;
      const data = readMeasurements();
      const classification = classify(data);
      const secondary = secondaryInfluence(classification.shape, data, classification);
      const profile = {
        version: ALGORITHM_VERSION,
        shape: classification.shape,
        secondary,
        proportion: proportionLabel(classification.shape, data, classification),
        fit: state.fit.length ? state.fit : ['none'],
        preference,
        guidance: guidanceChoice,
        intent,
        updated: new Date().toISOString().slice(0, 10)
      };
      storeProfile(profile);
      renderProfile(profile);
      emitEvent('blueprint_result_viewed', profile);
    }

    const start = root.querySelector('[data-start]');
    if (start) start.addEventListener('click', () => { state.refinementShown = false; showScreen('measurements'); emitEvent('blueprint_started'); });

    root.querySelectorAll('[data-back]').forEach((button) => {
      button.addEventListener('click', () => showScreen(button.dataset.back));
    });

    const measurementNext = root.querySelector('[data-measurements-next]');
    if (measurementNext) measurementNext.addEventListener('click', () => {
      const data = readMeasurements();
      if (!validateMeasurements(data)) return;
      const needs = determineRefinements(data);
      if (!state.refinementShown && (needs.shoulders || needs.midsection)) {
        showRefinements(needs);
        state.refinementShown = true;
        measurementNext.textContent = 'Continue to fit experience';
        return;
      }
      if (needs.shoulders && !(root.querySelector(`input[name^="ai-shoulders-"]:checked`))) {
        const error = root.querySelector('[data-measure-error]');
        error.textContent = 'Please choose the closest shoulder-to-hip description, or select "I\'m not sure."';
        error.hidden = false;
        return;
      }
      if (needs.midsection && !(root.querySelector(`input[name^="ai-midsection-compare-"]:checked`))) {
        const error = root.querySelector('[data-measure-error]');
        error.textContent = 'Please answer the midsection clarification, or select "I\'m not sure."';
        error.hidden = false;
        return;
      }
      state.measurements = data;
      showScreen('fit');
      emitEvent('blueprint_measurements_completed');
    });

    root.querySelectorAll('[data-fit]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const none = root.querySelector('[data-fit][value="none"]');
        if (checkbox.value === 'none' && checkbox.checked) {
          root.querySelectorAll('[data-fit]').forEach((item) => { if (item !== checkbox) item.checked = false; });
        } else if (checkbox.checked && none) {
          none.checked = false;
        }
        const after = Array.from(root.querySelectorAll('[data-fit]:checked'));
        if (after.length > 2) checkbox.checked = false;
      });
    });

    const fitNext = root.querySelector('[data-fit-next]');
    if (fitNext) fitNext.addEventListener('click', () => {
      const selected = Array.from(root.querySelectorAll('[data-fit]:checked')).map((el) => el.value);
      const error = root.querySelector('[data-fit-error]');
      error.hidden = true;
      if (!selected.length) {
        error.textContent = 'Please choose one or two fit situations, or select "No consistent pattern."';
        error.hidden = false;
        return;
      }
      state.fit = selected;
      showScreen('preference');
      emitEvent('blueprint_fit_completed');
    });

    const build = root.querySelector('[data-build-blueprint]');
    if (build) build.addEventListener('click', () => {
      emitEvent('blueprint_style_completed');
      buildProfile();
    });

    const retake = root.querySelector('[data-retake]');
    if (retake) retake.addEventListener('click', () => {
      clearStoredProfile();
      window.location.hash = '';
      window.location.reload();
    });

    const changeIntent = root.querySelector('[data-change-intent]');
    if (changeIntent) changeIntent.addEventListener('click', () => showScreen('preference'));

    const restore = root.querySelector('[data-restore-profile]');
    if (restore) restore.addEventListener('click', () => {
      const profile = getStoredProfile();
      if (profile && profile.shape && profile.preference && profile.guidance && profile.intent) renderProfile(profile);
    });

    const clear = root.querySelector('[data-clear-profile]');
    if (clear) clear.addEventListener('click', () => {
      clearStoredProfile();
      savedPanel.hidden = true;
    });

    const shapeLink = root.querySelector('[data-shape-link]');
    if (shapeLink) shapeLink.addEventListener('click', () => emitEvent('blueprint_shape_collection_clicked', getStoredProfile()));
    const intentLink = root.querySelector('[data-intent-link]');
    if (intentLink) intentLink.addEventListener('click', () => emitEvent('blueprint_category_clicked', getStoredProfile()));

    const emailForm = root.querySelector('.ai-bsc-v2__email-form');
    if (emailForm) emailForm.addEventListener('submit', () => emitEvent('blueprint_email_submitted', getStoredProfile()));

    const stored = getStoredProfile();
    if (stored && stored.shape && stored.preference && stored.guidance && stored.intent) savedPanel.hidden = false;
  }

  function initializeAll(container) {
    (container || document).querySelectorAll('[data-ai-blueprint-root]').forEach(initCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
